import {useCallback, useContext} from 'react';
import type {ContextType} from 'react';
import {matchRoutes, UNSAFE_DataRouterContext, useNavigate} from 'react-router';

import {useCurrentOrgReactPages} from '#queries/react_pages/index.js';

import type {LinkNavigateFn} from './types.js';
import {extractPathname, findRegistryMatch} from './utils.js';

function doesRouterContextMatchPath(
  ctx: ContextType<typeof UNSAFE_DataRouterContext>,
  pathname: string
): boolean {
  if (!ctx) return false;
  try {
    return matchRoutes(ctx.router.routes, pathname) !== null;
  } catch {
    return false;
  }
}

function isClientRouted(
  reactPages: ReturnType<typeof useCurrentOrgReactPages>['data'],
  ctx: ContextType<typeof UNSAFE_DataRouterContext>,
  pathname: string
): boolean {
  const registryMatch = findRegistryMatch(reactPages, pathname);

  if (registryMatch && !registryMatch.enabled) return false;
  return doesRouterContextMatchPath(ctx, pathname);
}

export function useIsHandledByRouter(href: string): boolean {
  // NOTE(joshua): UNSAFE_DataRouterContext is part of React Router's internals and
  // used by things like `useNavigate`, etc. Using it here is stable so long as its own
  // hooks remain stable.
  const ctx = useContext(UNSAFE_DataRouterContext);
  return doesRouterContextMatchPath(ctx, extractPathname(href));
}

export function useShouldBeClientRouted(
  href: string,
  options?: {hardLink?: boolean}
): boolean {
  const {data: reactPages} = useCurrentOrgReactPages();
  const ctx = useContext(UNSAFE_DataRouterContext);

  if (options?.hardLink) return false;

  return isClientRouted(reactPages, ctx, extractPathname(href));
}

/**
 * Wrapper around React Router's `useNavigate` for intelligently directing
 * or redirecting to legacy (i.e., Rails) pages.
 */
export function useLinkNavigate(): LinkNavigateFn {
  const navigate = useNavigate();
  const {data: reactPages} = useCurrentOrgReactPages();
  const ctx = useContext(UNSAFE_DataRouterContext);

  return useCallback(
    (href, event) => {
      if (event?.metaKey || event?.ctrlKey) {
        window.open(href, '_blank', 'noopener,noreferrer');
        return;
      }

      const pathname = extractPathname(href);
      const clientRouted = isClientRouted(reactPages, ctx, pathname);

      if (clientRouted) {
        navigate(href);
      } else {
        window.location.assign(href);
      }
    },
    [navigate, reactPages, ctx]
  );
}
